import { getTimezoneLabel } from './getTimezoneLabel';
import timezoneData from './timezone-data';
import { TimezoneOption } from './timezones';
import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment-timezone';

type TranslateFn = (key: string, opts?: { defaultValue: string }) => string;

/**
 * Builds the timezone picker options with localized labels.
 *
 * The label is `(GMT±hh:mm) <localized label>`; the localized portion comes from
 * {@link getTimezoneLabel} (i18n-backed), while `value`/`keywords` stay English so
 * timezone resolution and search keep working. This is a function (not a module-level
 * constant) so it recomputes against the active locale — callers should memoize it on
 * the current language.
 *
 * @param t - i18next translate function from `useTranslation()` or `i18n.t`.
 */
export const getTimezoneStrings = (t?: TranslateFn): TimezoneOption[] => {
  const now = new Date();
  const timezoneStrings: TimezoneOption[] = [];
  for (const { value, keywords } of timezoneData) {
    timezoneStrings.push({
      label: `(GMT${formatInTimeZone(now, value, 'xxx')}) ${getTimezoneLabel(value, t)}`,
      value,
      keywords: keywords || '',
      category: 'All Timezones',
    });
  }

  const timestamp = new Date().getTime();
  timezoneStrings.sort((a, b) => {
    return (moment.tz.zone(b.value)?.utcOffset(timestamp) ?? 0) - (moment.tz.zone(a.value)?.utcOffset(timestamp) ?? 0);
  });

  return timezoneStrings;
};
